import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ShieldCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const STORAGE_KEY = "km_cookie_consent";

export function getCookieConsent() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prefs, setPrefs] = useState({ statistics: false, marketing: false });

  useEffect(() => {
    const stored = getCookieConsent();
    if (!stored) setVisible(true);
    else setPrefs({ statistics: !!stored.statistics, marketing: !!stored.marketing });

    const openSettings = () => {
      const s = getCookieConsent();
      if (s) setPrefs({ statistics: !!s.statistics, marketing: !!s.marketing });
      setSettingsOpen(true);
    };
    window.addEventListener("open-cookie-settings", openSettings);
    return () => window.removeEventListener("open-cookie-settings", openSettings);
  }, []);

  const persist = (data) => {
    const payload = { necessary: true, ...data, ts: new Date().toISOString(), v: 1 };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setVisible(false);
    setSettingsOpen(false);
  };

  const acceptAll = () => persist({ statistics: true, marketing: true });
  const rejectAll = () => persist({ statistics: false, marketing: false });
  const saveSelection = () => persist(prefs);

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 bottom-0 z-[70] p-4 sm:p-5"
            data-testid="cookie-banner"
          >
            <div className="mx-auto max-w-[1100px] border border-white/10 bg-[#0a0a0a]/98 backdrop-blur-xl p-6 shadow-2xl sm:p-8">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center border border-volt/40 text-volt">
                    <Cookie className="h-6 w-6" />
                  </span>
                  <div>
                    <h3 className="font-heading text-xl font-bold uppercase text-white">Datenschutz & Cookies</h3>
                    <p className="mt-1.5 max-w-2xl font-body text-sm leading-relaxed text-neutral-400">
                      Wir verwenden notwendige Cookies für den Betrieb der Website. Optionale
                      Cookies (z. B. für Statistik) setzen wir nur mit deiner Einwilligung.
                      Mehr dazu in unserer{" "}
                      <Link to="/datenschutz" className="text-volt underline underline-offset-2 hover:text-white">
                        Datenschutzerklärung
                      </Link>.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-2.5 sm:flex-row lg:shrink-0">
                  <button
                    onClick={() => setSettingsOpen(true)}
                    data-testid="cookie-settings-open"
                    className="order-3 border border-white/15 px-5 py-3 font-body text-sm font-bold uppercase tracking-wide text-neutral-300 transition-colors hover:border-white/40 hover:text-white sm:order-1"
                  >
                    Einstellungen
                  </button>
                  <button
                    onClick={rejectAll}
                    data-testid="cookie-reject-all"
                    className="order-2 border border-white/15 px-5 py-3 font-body text-sm font-bold uppercase tracking-wide text-neutral-300 transition-colors hover:border-white/40 hover:text-white"
                  >
                    Ablehnen
                  </button>
                  <button
                    onClick={acceptAll}
                    data-testid="cookie-accept-all"
                    className="order-1 bg-volt px-6 py-3 font-body text-sm font-bold uppercase tracking-wide text-black transition-transform hover:scale-[1.03] glow-volt sm:order-3"
                  >
                    Alle akzeptieren
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg border-white/10 bg-[#0a0a0a] text-white" data-testid="cookie-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-extrabold uppercase text-white">
              Cookie-Einstellungen
            </DialogTitle>
            <DialogDescription className="font-body text-neutral-400">
              Wähle, welche Cookies du zulassen möchtest. Deine Auswahl kannst du jederzeit ändern.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 space-y-3">
            <div className="flex items-start justify-between gap-4 border border-white/10 bg-[#0d0d0d] p-4">
              <div>
                <p className="flex items-center gap-2 font-heading text-base font-bold uppercase text-white">
                  <ShieldCheck className="h-4 w-4 text-volt" /> Notwendig
                </p>
                <p className="mt-1 font-body text-xs text-neutral-400">
                  Für die Grundfunktionen der Website erforderlich. Immer aktiv.
                </p>
              </div>
              <Switch checked disabled data-testid="cookie-switch-necessary" />
            </div>

            <div className="flex items-start justify-between gap-4 border border-white/10 bg-[#0d0d0d] p-4">
              <div>
                <p className="font-heading text-base font-bold uppercase text-white">Statistik</p>
                <p className="mt-1 font-body text-xs text-neutral-400">
                  Hilft uns, die Nutzung der Website anonym auszuwerten und zu verbessern.
                </p>
              </div>
              <Switch
                checked={prefs.statistics}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, statistics: v }))}
                data-testid="cookie-switch-statistics"
              />
            </div>

            <div className="flex items-start justify-between gap-4 border border-white/10 bg-[#0d0d0d] p-4">
              <div>
                <p className="font-heading text-base font-bold uppercase text-white">Marketing</p>
                <p className="mt-1 font-body text-xs text-neutral-400">
                  Für personalisierte Inhalte und Reichweitenmessung von Kampagnen.
                </p>
              </div>
              <Switch
                checked={prefs.marketing}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                data-testid="cookie-switch-marketing"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
            <button
              onClick={saveSelection}
              data-testid="cookie-save-settings"
              className="flex-1 border border-white/15 px-5 py-3 font-body text-sm font-bold uppercase tracking-wide text-neutral-200 transition-colors hover:border-white/40 hover:text-white"
            >
              Auswahl speichern
            </button>
            <button
              onClick={acceptAll}
              data-testid="cookie-settings-accept-all"
              className="flex-1 bg-volt px-5 py-3 font-body text-sm font-bold uppercase tracking-wide text-black transition-transform hover:scale-[1.02]"
            >
              Alle akzeptieren
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
